
/**
 * Utility for consistent time formatting throughout the application
 * Converts 24-hour format times to 12-hour format with AM/PM
 * Removes seconds from display
 */

/**
 * Format a single time string to 12-hour format
 * @param time Time string in hh:mm:ss or hh:mm format
 * @returns Formatted time string in h:mm AM/PM format
 */
export const formatSingleTime = (time?: string | null): string => {
  if (!time) return '';
  
  // If the time already includes AM/PM, return it as is
  if (time.includes('AM') || time.includes('PM')) {
    return time;
  }

  try {
    // Split the time by : and handle different formats
    const parts = time.split(':');
    if (parts.length < 2) return time; // Invalid format, return original
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) return time; // Invalid format, return original
    
    // Convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    // Format with leading zero for minutes but not for hours
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    
    return `${displayHours}:${formattedMinutes} ${period}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return time; // Return original on error
  }
};

/**
 * Format a time range to 12-hour format
 * @param startTime Start time string
 * @param endTime End time string
 * @param separator Separator between times (default: " - ")
 * @returns Formatted time range string
 */
export const formatTimeRange = (
  startTime?: string | null, 
  endTime?: string | null, 
  separator: string = " - "
): string => {
  if (!startTime) return '';
  if (!endTime) return formatSingleTime(startTime);
  
  return `${formatSingleTime(startTime)}${separator}${formatSingleTime(endTime)}`;
};

/**
 * Format a time string that might be a range (containing "to", "-", or other separators)
 * @param timeString Time string that might contain a range
 * @returns Formatted time string or range
 */
export const formatTimeString = (timeString?: string | null): string => {
  if (!timeString) return '';
  
  // Already has AM/PM formatting
  if (timeString.includes('AM') || timeString.includes('PM')) {
    return timeString;
  }
  
  // Check for common time range separators
  if (timeString.includes(' to ')) {
    const [start, end] = timeString.split(' to ');
    return formatTimeRange(start, end);
  }
  
  if (timeString.includes(' - ')) {
    const [start, end] = timeString.split(' - ');
    return formatTimeRange(start, end);
  }
  
  // If no range separator found, treat as single time
  return formatSingleTime(timeString);
};
